import * as THREE from 'three';
import { type V2, angleDiff, clamp, damp, lerp } from '../../core/math';
import { hashString } from '../../core/rng';
import type { ZoneRuntime } from '../../game/ZoneRuntime';
import type { AssetLibrary } from '../../render/assets';
import type { EntityId } from '../../sim/types';
import { SHIP_DRAFT, SHIP_MODEL, type WreckSite } from './data';
import { HAZARDS, type Hazard, sampleHazards, stormCurrent } from './hazards';
import type { SalvageHow, SeaFx, SeaRuntime } from './runtime';

/**
 * Sea presentation (browser only): storm cells (dark water, swirling cloud
 * canopy, rain and lightning), drifting fog banks, wreck props with beacons,
 * foam wakes behind every hull, hull bob/roll/heel, and the zone's fog and
 * light grading while sailing through weather. Everything lives in the zone's
 * own scene and is disposed with it; nothing here affects the simulation.
 */

const STORM_FOG = new THREE.Color(0x27313b);
const STORM_BG = new THREE.Color(0x1c242c);
const FOG_COLOR = new THREE.Color(0xc5d0d8);
const WAKE_MAX = 320;
const RAIN_DROPS = 1100;
const RAIN_BOX = { x: 38, y: 32, z: 38 };

function canvasTexture(size: number, paint: (g: CanvasRenderingContext2D, s: number) => void): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');
  if (g) paint(g, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Soft round puff (fog, foam). */
const puffTexture = (): THREE.CanvasTexture =>
  canvasTexture(64, (g, s) => {
    const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    grd.addColorStop(0, 'rgba(255,255,255,1)');
    grd.addColorStop(0.45, 'rgba(255,255,255,0.55)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, s, s);
  });

/** Lumpy cloud made of overlapping blobs (deterministic). */
const cloudTexture = (): THREE.CanvasTexture =>
  canvasTexture(128, (g, s) => {
    let seed = 7;
    const rnd = () => {
      seed = (seed * 16807) % 2147483647;
      return seed / 2147483647;
    };
    for (let i = 0; i < 14; i++) {
      const x = s * (0.25 + rnd() * 0.5);
      const y = s * (0.3 + rnd() * 0.4);
      const r = s * (0.14 + rnd() * 0.2);
      const grd = g.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, 'rgba(255,255,255,0.55)');
      grd.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grd;
      g.fillRect(0, 0, s, s);
    }
  });

/** Dark centre fading to nothing (storm-shadowed water). */
const shadowTexture = (): THREE.CanvasTexture =>
  canvasTexture(128, (g, s) => {
    const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    grd.addColorStop(0, 'rgba(255,255,255,1)');
    grd.addColorStop(0.7, 'rgba(255,255,255,0.75)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, s, s);
  });

/** Vertical fade for beacon beams (bright at the base). */
const beamTexture = (): THREE.CanvasTexture =>
  canvasTexture(64, (g, s) => {
    const grd = g.createLinearGradient(0, s, 0, 0);
    grd.addColorStop(0, 'rgba(255,255,255,1)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, s, s);
  });

/** Small deterministic PRNG per id (layouts identical on every peer). */
function seeded(id: string): () => number {
  let s = hashString(id) || 1;
  return () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
}

interface WreckVisual {
  root: THREE.Group;
  phase: number;
}

interface Bolt {
  line: THREE.Line;
  t: number;
}

interface Foam {
  x: number;
  z: number;
  vx: number;
  vz: number;
  age: number;
  life: number;
  s0: number;
  s1: number;
  a: number;
  rot: number;
}

/** Wraps `v` into [c - half, c + half). */
function wrapInto(v: number, c: number, half: number): number {
  const span = half * 2;
  return c + ((((v - c + half) % span) + span) % span) - half;
}

export interface SeaFrame {
  /** Camera → focus distance (fog must start beyond the ship). */
  camDist: number;
  /** Crew lookout bonus (0..1): thins fog banks. */
  fogSight: number;
}

export class SeaScene implements SeaFx {
  private readonly group = new THREE.Group();
  private readonly owned: { dispose(): void }[] = [];
  private readonly wrecks = new Map<string, WreckVisual>();
  private readonly clouds: { group: THREE.Group; mat: THREE.SpriteMaterial }[] = [];
  private readonly banks: { sprites: THREE.Sprite[]; mat: THREE.SpriteMaterial; hazard: Hazard }[] = [];
  private readonly bolts: Bolt[] = [];
  private readonly heel = new Map<EntityId, number>();
  private readonly lastFacing = new Map<EntityId, number>();
  private readonly base: { near: number; far: number; color: THREE.Color; bg: THREE.Color | null; sun: number; hemi: number } | null;
  private readonly tex: { puff: THREE.Texture; cloud: THREE.Texture; shadow: THREE.Texture; beam: THREE.Texture };
  private readonly beamGeo = new THREE.CylinderGeometry(0.22, 0.22, 16, 8, 1, true);
  private readonly beamMats: Record<WreckSite['kind'], THREE.MeshBasicMaterial>;
  private rain: THREE.LineSegments;
  private rainPos: Float32Array;
  private rainMat: THREE.LineBasicMaterial;
  private wake: THREE.InstancedMesh;
  private wakeParts: Foam[] = [];
  private wakeT = 0;
  private readonly tmpM = new THREE.Matrix4();
  private readonly tmpQ = new THREE.Quaternion();
  private readonly tmpV = new THREE.Vector3();
  private readonly tmpS = new THREE.Vector3();
  private readonly tmpC = new THREE.Color();
  private time = 0;
  stormF = 0;
  fogF = 0;
  private disposed = false;

  constructor(
    private readonly z: ZoneRuntime,
    private readonly assets: AssetLibrary,
    private readonly rt: SeaRuntime,
  ) {
    this.group.name = 'sailing';
    z.scene.add(this.group);
    this.tex = { puff: puffTexture(), cloud: cloudTexture(), shadow: shadowTexture(), beam: beamTexture() };
    this.owned.push(this.tex.puff, this.tex.cloud, this.tex.shadow, this.tex.beam, this.beamGeo);
    const beam = (color: number, opacity: number) => new THREE.MeshBasicMaterial({ color, map: this.tex.beam, transparent: true, opacity, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, fog: false });
    this.beamMats = { flotsam: beam(0x9fe0ff, 0.45), derelict: beam(0xffc860, 0.7) };
    this.owned.push(this.beamMats.flotsam, this.beamMats.derelict);
    const fog = z.scene.fog;
    this.base =
      fog instanceof THREE.Fog
        ? { near: fog.near, far: fog.far, color: fog.color.clone(), bg: z.scene.background instanceof THREE.Color ? z.scene.background.clone() : null, sun: z.lighting.sun.intensity, hemi: z.lighting.hemi.intensity }
        : null;
    for (const h of HAZARDS) {
      if (h.kind === 'storm') this.buildStorm(h);
      else this.buildFogBank(h);
    }
    const rain = this.buildRain();
    this.rain = rain.lines;
    this.rainPos = rain.pos;
    this.rainMat = rain.mat;
    this.wake = this.buildWake();
    for (const site of rt.wrecks.values()) this.wreckAdded(site);
  }

  // ---------------------------------------------------------------- build

  private buildStorm(h: Hazard): void {
    const shade = new THREE.Mesh(
      new THREE.CircleGeometry(h.r * 1.08, 64),
      new THREE.MeshBasicMaterial({ color: 0x07101a, map: this.tex.shadow, transparent: true, opacity: 0.5, depthWrite: false }),
    );
    shade.rotation.x = -Math.PI / 2;
    shade.position.set(h.x, 0.06, h.z);
    shade.renderOrder = 2;
    this.group.add(shade);
    this.owned.push(shade.geometry, shade.material);
    const rnd = seeded(h.id);
    const mat = new THREE.SpriteMaterial({ map: this.tex.cloud, color: 0x3a434d, transparent: true, opacity: 0.62, depthWrite: false });
    this.owned.push(mat);
    const canopy = new THREE.Group();
    canopy.position.set(h.x, 0, h.z);
    for (let i = 0; i < 11; i++) {
      const a = rnd() * Math.PI * 2;
      const r = Math.sqrt(rnd()) * h.r * 0.85;
      const sp = new THREE.Sprite(mat);
      const size = 26 + rnd() * 20;
      sp.scale.set(size, size * 0.7, 1);
      sp.position.set(Math.sin(a) * r, 19 + rnd() * 8, Math.cos(a) * r);
      sp.renderOrder = 6;
      canopy.add(sp);
    }
    this.group.add(canopy);
    this.clouds.push({ group: canopy, mat });
  }

  private buildFogBank(h: Hazard): void {
    const rnd = seeded(h.id);
    const mat = new THREE.SpriteMaterial({ map: this.tex.puff, color: 0xe6ecf0, transparent: true, opacity: 0.3, depthWrite: false });
    this.owned.push(mat);
    const sprites: THREE.Sprite[] = [];
    for (let i = 0; i < 16; i++) {
      const a = rnd() * Math.PI * 2;
      const r = Math.sqrt(rnd()) * h.r * 0.9;
      const sp = new THREE.Sprite(mat);
      const size = 24 + rnd() * 22;
      sp.scale.set(size, size * 0.55, 1);
      sp.position.set(h.x + Math.sin(a) * r, 2.5 + rnd() * 5, h.z + Math.cos(a) * r);
      sp.userData.home = sp.position.clone();
      sp.userData.phase = rnd() * 10;
      sp.renderOrder = 5;
      this.group.add(sp);
      sprites.push(sp);
    }
    this.banks.push({ sprites, mat, hazard: h });
  }

  private buildRain(): { lines: THREE.LineSegments; pos: Float32Array; mat: THREE.LineBasicMaterial } {
    const pos = new Float32Array(RAIN_DROPS * 6);
    for (let i = 0; i < RAIN_DROPS; i++) {
      const x = (Math.random() * 2 - 1) * RAIN_BOX.x;
      const y = Math.random() * RAIN_BOX.y;
      const z = (Math.random() * 2 - 1) * RAIN_BOX.z;
      pos.set([x, y, z, x, y - 1.2, z], i * 6);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const mat = new THREE.LineBasicMaterial({ color: 0xa9bccc, transparent: true, opacity: 0, depthWrite: false });
    const lines = new THREE.LineSegments(geo, mat);
    lines.frustumCulled = false;
    lines.visible = false;
    lines.renderOrder = 7;
    this.group.add(lines);
    this.owned.push(geo, mat);
    return { lines, pos, mat };
  }

  private buildWake(): THREE.InstancedMesh {
    const geo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    const mat = new THREE.MeshBasicMaterial({ map: this.tex.puff, color: 0xffffff, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    const mesh = new THREE.InstancedMesh(geo, mat, WAKE_MAX);
    mesh.frustumCulled = false;
    mesh.renderOrder = 3;
    this.tmpM.makeScale(0, 0, 0);
    for (let i = 0; i < WAKE_MAX; i++) {
      mesh.setMatrixAt(i, this.tmpM);
      mesh.setColorAt(i, this.tmpC.setRGB(0, 0, 0));
    }
    this.group.add(mesh);
    this.owned.push(geo, mat);
    return mesh;
  }

  // ---------------------------------------------------------------- SeaFx

  wreckAdded(site: WreckSite): void {
    if (this.disposed || this.wrecks.has(site.id)) return;
    const root = new THREE.Group();
    root.position.set(site.x, this.rt.seaLevel, site.z);
    const beam = new THREE.Mesh(this.beamGeo, this.beamMats[site.kind]);
    beam.position.y = 8;
    beam.renderOrder = 8;
    root.add(beam);
    this.group.add(root);
    const vis: WreckVisual = { root, phase: seeded(site.id)() * 10 };
    this.wrecks.set(site.id, vis);
    const rnd = seeded(`${site.id}:props`);
    const pieces: { model: string; x: number; z: number; y: number; rx: number; ry: number; rz: number; s: number }[] =
      site.kind === 'derelict'
        ? [
            { model: SHIP_MODEL, x: 0, z: 0, y: -2.1, rx: 0.08, ry: rnd() * 6.28, rz: 0.42, s: 0.72 },
            { model: 'chest_closed', x: 2.6, z: 1.2, y: -0.25, rx: 0.1, ry: rnd() * 6.28, rz: 0.15, s: 1.1 },
            { model: 'barrel', x: -3.2, z: 2.4, y: -0.45, rx: 1.3, ry: rnd() * 6.28, rz: 0, s: 1.1 },
            { model: 'crate', x: 3.4, z: -2.6, y: -0.35, rx: 0.2, ry: rnd() * 6.28, rz: 0.3, s: 1.1 },
          ]
        : [
            { model: 'crate', x: 0.6, z: 0.2, y: -0.35, rx: 0.15, ry: rnd() * 6.28, rz: 0.2, s: 1.2 },
            { model: 'crate', x: -1.4, z: 1.3, y: -0.4, rx: -0.2, ry: rnd() * 6.28, rz: 0.1, s: 1 },
            { model: 'barrel', x: 1.9, z: -1.6, y: -0.45, rx: 1.4, ry: rnd() * 6.28, rz: 0, s: 1.1 },
            { model: 'barrel', x: -2.2, z: -0.9, y: -0.55, rx: 1.2, ry: rnd() * 6.28, rz: 0.4, s: 1 },
            { model: 'log', x: 0.2, z: 2.6, y: -0.3, rx: 0, ry: rnd() * 6.28, rz: 0.1, s: 1.2 },
          ];
    for (const pc of pieces) {
      void this.assets.load(pc.model).then((t) => {
        if (this.disposed || this.wrecks.get(site.id) !== vis) return;
        const o = this.assets.instantiate(t);
        o.position.set(pc.x, pc.y, pc.z);
        o.rotation.set(pc.rx, pc.ry, pc.rz);
        o.scale.setScalar(pc.s);
        vis.root.add(o);
      });
    }
  }

  wreckRemoved(site: WreckSite, how: SalvageHow): void {
    const vis = this.wrecks.get(site.id);
    if (!vis) return;
    this.wrecks.delete(site.id);
    // Model instances share the asset cache's geometry/materials: detach only.
    this.group.remove(vis.root);
    const hue = how === 'cannon' ? 0xffa040 : 0xffd070;
    this.z.vfx.handle({ type: 'areaErupt', id: -1, origin: { x: site.x, z: site.z }, facing: 0, shape: { kind: 'circle', radius: site.kind === 'derelict' ? 4 : 2.5 }, vfx: how === 'cannon' ? 'pillar' : undefined, hue });
    this.z.vfx.handle({ type: 'action', actorId: -1, castId: 0, skillId: '', kind: 'self', origin: { x: site.x, z: site.z }, facing: 0, hue });
    if (how === 'cannon') this.z.shake(0.25);
  }

  lightning(at: V2, hit: boolean): void {
    if (this.disposed) return;
    const pts: THREE.Vector3[] = [];
    const top = 46;
    let x = at.x + (Math.random() - 0.5) * 8;
    let zz = at.z + (Math.random() - 0.5) * 8;
    for (let i = 0; i <= 9; i++) {
      const t = i / 9;
      pts.push(new THREE.Vector3(lerp(x, at.x, t), top * (1 - t) + this.rt.seaLevel, lerp(zz, at.z, t)));
      x += (Math.random() - 0.5) * 3.2;
      zz += (Math.random() - 0.5) * 3.2;
    }
    const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), new THREE.LineBasicMaterial({ color: 0xf2f8ff, transparent: true, opacity: 1, fog: false }));
    line.frustumCulled = false;
    line.renderOrder = 9;
    this.group.add(line);
    this.bolts.push({ line, t: 0.22 });
    this.z.vfx.handle({ type: 'areaErupt', id: -1, origin: at, facing: 0, shape: { kind: 'circle', radius: hit ? 3 : 2 }, vfx: 'pillar', hue: 0xcfe4ff });
    this.z.screenFlash(0xe8f0ff, hit ? 0.55 : 0.28, 0.22);
    this.z.shake(hit ? 0.55 : 0.18);
  }

  aground(at: V2): void {
    this.z.vfx.handle({ type: 'areaErupt', id: -1, origin: at, facing: 0, shape: { kind: 'circle', radius: 2.2 }, hue: 0xd8c8a0 });
    this.z.shake(0.4);
  }

  // ---------------------------------------------------------------- per frame

  frame(dt: number, f: SeaFrame): void {
    if (this.disposed) return;
    this.time += dt;
    const hz = this.rt.hazards;
    this.stormF += (hz.storm - this.stormF) * damp(1.6, dt);
    this.fogF += (hz.fog * (1 - 0.6 * f.fogSight) - this.fogF) * damp(1.3, dt);
    this.grade(f.camDist);
    this.bobShips(dt);
    this.updateWake(dt);
    this.updateRain(dt);
    this.updateSkies(dt);
    this.updateWrecks();
    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const b = this.bolts[i] as Bolt;
      b.t -= dt;
      (b.line.material as THREE.LineBasicMaterial).opacity = Math.max(0, b.t / 0.22) * (0.6 + 0.4 * Math.sin(b.t * 90));
      if (b.t <= 0) {
        this.group.remove(b.line);
        b.line.geometry.dispose();
        (b.line.material as THREE.Material).dispose();
        this.bolts.splice(i, 1);
      }
    }
  }

  /** Weather grading: murky dark under storms, a white wall in fog banks; the helm view itself stays clear. */
  private grade(camDist: number): void {
    const b = this.base;
    const fog = this.z.scene.fog;
    if (!b || !(fog instanceof THREE.Fog)) return;
    const s = this.stormF;
    const f = this.fogF;
    let near = Math.max(b.near, camDist * 0.95);
    let far = Math.max(b.far, camDist + 180);
    near = lerp(near, camDist + 4, s * 0.7);
    far = lerp(far, camDist + 110, s);
    near = lerp(near, Math.max(4, camDist - 3), f);
    far = lerp(far, camDist + 30, f);
    fog.near = near;
    fog.far = Math.max(near + 1, far);
    fog.color.copy(b.color).lerp(STORM_FOG, s * 0.85).lerp(FOG_COLOR, f);
    if (b.bg && this.z.scene.background instanceof THREE.Color) this.z.scene.background.copy(b.bg).lerp(STORM_BG, s * 0.85).lerp(FOG_COLOR, f);
    this.z.lighting.sun.intensity = b.sun * (1 - 0.62 * s) * (1 - 0.3 * f);
    this.z.lighting.hemi.intensity = b.hemi * (1 - 0.35 * s);
  }

  /** Hulls ride the swell: draft, bob, roll and pitch (more in storms) and a heel into turns. */
  private bobShips(dt: number): void {
    const own = this.rt.ship;
    for (const a of this.z.world.actors.values()) {
      if (a.look.model !== SHIP_MODEL || !a.alive) continue;
      const v = this.z.views.get(a.id);
      if (!v || v.modelId !== SHIP_MODEL) continue;
      const root = v.root;
      if (root.rotation.order !== 'YXZ') root.rotation.order = 'YXZ';
      const local = own?.actor === a;
      const storm = local ? this.stormF : sampleHazards(a.pos).storm;
      const amp = 1 + 2.2 * storm;
      const t = this.time + a.id * 0.37;
      const prev = this.lastFacing.get(a.id) ?? a.facing;
      this.lastFacing.set(a.id, a.facing);
      const turn = local && own ? own.motion.turnVel : angleDiff(prev, a.facing) / Math.max(dt, 1e-3);
      const h = (this.heel.get(a.id) ?? 0) + (clamp(turn, -1.2, 1.2) * Math.min(1, a.speed / 6) - (this.heel.get(a.id) ?? 0)) * damp(2.5, dt);
      this.heel.set(a.id, h);
      root.position.y += SHIP_DRAFT + (Math.sin(t * 1.15) * 0.14 + Math.sin(t * 2.3) * 0.04) * amp;
      root.rotation.z = Math.sin(t * 0.85) * 0.03 * amp + h * 0.09;
      root.rotation.x = Math.sin(t * 0.7 + 1.3) * 0.018 * amp;
    }
  }

  private updateWake(dt: number): void {
    this.wakeT -= dt;
    if (this.wakeT <= 0) {
      this.wakeT = 0.08;
      for (const a of this.z.world.actors.values()) {
        if (a.look.model !== SHIP_MODEL || !a.alive || a.speed < 1.2) continue;
        const v = this.z.views.get(a.id);
        const px = v ? v.root.position.x : a.pos.x;
        const pz = v ? v.root.position.z : a.pos.z;
        const fx = Math.sin(a.facing);
        const fz = Math.cos(a.facing);
        // Starboard/port unit vector (perpendicular to the keel).
        const sx = -fz;
        const sz = fx;
        const k = Math.min(1, a.speed / 9);
        // Centre wash off the stern, then a spreading V from both quarters and spray at the bow.
        this.spawnFoam(px - fx * 5.6, pz - fz * 5.6, -fx * 0.4, -fz * 0.4, 2.2, 4.6, 1.3, 0.03 * k + 0.01);
        for (const side of [-1, 1]) {
          this.spawnFoam(px - fx * 4.6 + sx * side * 1.5, pz - fz * 4.6 + sz * side * 1.5, sx * side * 2.2 - fx * 0.3, sz * side * 2.2 - fz * 0.3, 1.4, 6, 2, 0.04 * k + 0.008);
          this.spawnFoam(px + fx * 5 + sx * side * 1.3, pz + fz * 5 + sz * side * 1.3, sx * side * 2.6, sz * side * 2.6, 0.9, 2.8, 0.7, 0.06 * k);
        }
      }
    }
    const y = this.rt.seaLevel + 0.07;
    let n = 0;
    for (let i = this.wakeParts.length - 1; i >= 0; i--) {
      const p = this.wakeParts[i] as Foam;
      p.age += dt;
      p.x += p.vx * dt;
      p.z += p.vz * dt;
      if (p.age >= p.life) this.wakeParts.splice(i, 1);
    }
    for (const p of this.wakeParts) {
      const t = p.age / p.life;
      const s = lerp(p.s0, p.s1, t);
      this.tmpQ.setFromAxisAngle(this.tmpV.set(0, 1, 0), p.rot);
      this.tmpM.compose(this.tmpV.set(p.x, y, p.z), this.tmpQ, this.tmpS.set(s, 1, s));
      this.wake.setMatrixAt(n, this.tmpM);
      const a = p.a * (1 - t) * (1 - t);
      this.wake.setColorAt(n, this.tmpC.setRGB(a, a, a));
      n++;
    }
    this.tmpM.makeScale(0, 0, 0);
    for (let i = n; i < this.wake.count; i++) this.wake.setMatrixAt(i, this.tmpM);
    this.wake.instanceMatrix.needsUpdate = true;
    if (this.wake.instanceColor) this.wake.instanceColor.needsUpdate = true;
  }

  private spawnFoam(x: number, z: number, vx: number, vz: number, s0: number, s1: number, life: number, a: number): void {
    if (a <= 0.005) return;
    if (this.wakeParts.length >= WAKE_MAX) this.wakeParts.shift();
    this.wakeParts.push({ x, z, vx, vz, age: 0, life: life * (0.8 + Math.random() * 0.4), s0, s1, a, rot: Math.random() * Math.PI * 2 });
  }

  private updateRain(dt: number): void {
    const s = this.stormF;
    const own = this.rt.ship?.actor;
    this.rain.visible = s > 0.02 && !!own;
    if (!this.rain.visible || !own) return;
    this.rainMat.opacity = Math.min(0.55, s * 0.6);
    const v = this.z.views.get(own.id);
    const cx = v ? v.root.position.x : own.pos.x;
    const cz = v ? v.root.position.z : own.pos.z;
    const hz = this.rt.hazards.stormHazard;
    const wind = hz ? stormCurrent(hz, own.pos, 1) : { x: 0, z: 0 };
    const fall = 34 * dt;
    const slant = 0.09;
    const pos = this.rainPos;
    for (let i = 0; i < RAIN_DROPS; i++) {
      const o = i * 6;
      let x = (pos[o] as number) + wind.x * slant * fall;
      let y = (pos[o + 1] as number) - fall;
      let z = (pos[o + 2] as number) + wind.z * slant * fall;
      // Keep the drops in a box that follows the ship (wrap horizontally, respawn at the top).
      x = wrapInto(x, cx, RAIN_BOX.x);
      z = wrapInto(z, cz, RAIN_BOX.z);
      if (y < this.rt.seaLevel) {
        y += RAIN_BOX.y;
        x = cx + (Math.random() * 2 - 1) * RAIN_BOX.x;
        z = cz + (Math.random() * 2 - 1) * RAIN_BOX.z;
      }
      pos[o] = x;
      pos[o + 1] = y;
      pos[o + 2] = z;
      pos[o + 3] = x - wind.x * slant * 1.2;
      pos[o + 4] = y - 1.3;
      pos[o + 5] = z - wind.z * slant * 1.2;
    }
    (this.rain.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }

  /** Canopies swirl and thin out overhead (so they don't hide the ship); fog banks drift. */
  private updateSkies(dt: number): void {
    for (const c of this.clouds) {
      c.group.rotation.y += dt * 0.04;
      c.mat.opacity = lerp(0.62, 0.16, clamp(this.stormF * 1.6, 0, 1));
    }
    for (const b of this.banks) {
      b.mat.opacity = lerp(0.32, 0.14, clamp(this.fogF * 1.4, 0, 1));
      for (const sp of b.sprites) {
        const home = sp.userData.home as THREE.Vector3;
        const ph = sp.userData.phase as number;
        sp.position.set(home.x + Math.sin(this.time * 0.07 + ph) * 4, home.y + Math.sin(this.time * 0.3 + ph) * 0.4, home.z + Math.cos(this.time * 0.05 + ph) * 4);
      }
    }
  }

  private updateWrecks(): void {
    for (const w of this.wrecks.values()) {
      const t = this.time + w.phase;
      w.root.position.y = this.rt.seaLevel + Math.sin(t * 1.1) * 0.12;
      w.root.rotation.z = Math.sin(t * 0.8) * 0.04;
      w.root.rotation.x = Math.sin(t * 0.6 + 1) * 0.03;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const b = this.base;
    const fog = this.z.scene.fog;
    if (b && fog instanceof THREE.Fog) {
      fog.near = b.near;
      fog.far = b.far;
      fog.color.copy(b.color);
      if (b.bg && this.z.scene.background instanceof THREE.Color) this.z.scene.background.copy(b.bg);
      this.z.lighting.sun.intensity = b.sun;
      this.z.lighting.hemi.intensity = b.hemi;
    }
    for (const bolt of this.bolts) {
      bolt.line.geometry.dispose();
      (bolt.line.material as THREE.Material).dispose();
    }
    this.bolts.length = 0;
    this.z.scene.remove(this.group);
    this.wake.dispose();
    for (const o of this.owned) o.dispose();
    this.wrecks.clear();
  }
}
